import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { S3Bucket } from '../../types/api';
import Logger from '../../utils/logger';
import { getErrorMessage } from '../../utils/errorHandler';

interface CreateBucketModalProps {
  organisationId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (bucket: S3Bucket) => void;
}

interface FormValues {
  name: string;
  description: string;
  quota_gb: string;
}

const CreateBucketModal: React.FC<CreateBucketModalProps> = ({ organisationId, isOpen, onClose, onCreated }) => {
  const { t } = (useTranslation as any)('nubis3');
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const quotaBytes = values.quota_gb && values.quota_gb.trim().length > 0
        ? Number(values.quota_gb) * 1024 * 1024 * 1024
        : null;
      const bucket = await apiService.createS3Bucket({
        organisation_id: organisationId,
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        quota_bytes: quotaBytes,
      });
      toast.success(t('bucket.create.success', { name: bucket.name }));
      reset();
      onCreated(bucket);
      onClose();
    } catch (err: unknown) {
      Logger.error('Create S3 bucket failed', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (err as any)?.response?.status;
      const msg = getErrorMessage(err, t('bucket.create.errorDefault') as string);
      if (status === 403 && msg.toLowerCase().includes('quota')) {
        toast.error(t('errors.quotaReached'));
      } else if (status === 403) {
        toast.error(t('errors.planForbidden'));
      } else if (status === 409) {
        toast.error(t('errors.duplicateName'));
      } else if (status === 502 || status === 503) {
        toast.error(t('errors.b2Unavailable'));
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-1 rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-1">
          <h2 className="text-lg font-semibold text-text-primary">{t('bucket.create.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              {t('bucket.name')} *
            </label>
            <input
              type="text"
              placeholder={t('bucket.namePlaceholder') as string}
              {...register('name', {
                required: true,
                pattern: /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/,
                minLength: 3,
                maxLength: 63,
              })}
              className="w-full px-3 py-2 rounded-md border border-border-1 bg-surface-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
            <p className="mt-1 text-xs text-text-secondary">{t('bucket.nameHelp')}</p>
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{t('bucket.nameHelp')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              {t('bucket.description')}
            </label>
            <textarea
              rows={2}
              placeholder={t('bucket.descriptionPlaceholder') as string}
              {...register('description', { maxLength: 500 })}
              className="w-full px-3 py-2 rounded-md border border-border-1 bg-surface-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              {t('bucket.quota')}
            </label>
            <input
              type="number"
              min={0}
              step={1}
              placeholder={t('bucket.quotaPlaceholder') as string}
              {...register('quota_gb', { min: 0 })}
              className="w-full px-3 py-2 rounded-md border border-border-1 bg-surface-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium text-text-secondary hover:bg-surface-2"
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50"
            >
              {submitting ? t('bucket.create.submitting') : t('bucket.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBucketModal;
